import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, map, tap, catchError, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { LoginRequest, LoginResponse, TokenResponse, CurrentUserResponse } from '../../../../../shared/src/types/auth';
import { isStaffUser, resolveLanding } from './landing-policy';

export type LogoutReason = 'logout' | 'expired' | 'session-expired' | 'idle-timeout';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly apiUrl = environment.apiBaseUrl;
  private readonly websiteUrl = (environment as any).websiteUrl || 'https://www.ringoffice.com';

  private readonly accessToken$ = new BehaviorSubject<string | null>(null);
  private readonly currentUser$ = new BehaviorSubject<CurrentUserResponse | null>(null);

  readonly isAuthenticated$: Observable<boolean> = this.accessToken$.pipe(
    map(token => token !== null),
  );

  readonly user$: Observable<CurrentUserResponse | null> = this.currentUser$.asObservable();

  constructor() {
    // Keep signal in sync with BehaviorSubject
    this.currentUser$.subscribe(u => this._currentUser.set(u));
  }

  login(email: string, password: string): Observable<LoginResponse> {
    const body: LoginRequest = { email, password };
    return this.http
      .post<LoginResponse>(`${this.apiUrl}/auth/login`, body, { withCredentials: true })
      .pipe(
        tap(response => {
          this.accessToken$.next(response.accessToken);
          this.currentUser$.next(response.user);
        }),
      );
  }

  refresh(options: { redirectOnFailure?: boolean } = {}): Observable<TokenResponse | null> {
    return this.http
      .post<TokenResponse>(`${this.apiUrl}/auth/refresh`, {}, { withCredentials: true })
      .pipe(
        tap(response => {
          this.accessToken$.next(response.accessToken);
        }),
        catchError(() => {
          if (options.redirectOnFailure === false) {
            this.clearLocalSession();
          } else {
            this.handleSessionExpired();
          }
          return of(null);
        }),
      );
  }

  logout(reason: LogoutReason = 'logout'): Observable<void> {
    return this.http
      .post<void>(`${this.apiUrl}/auth/logout`, {}, { withCredentials: true })
      .pipe(
        tap(() => this.clearSession(reason)),
        catchError(() => {
          this.clearSession(reason);
          return of(undefined);
        }),
      );
  }

  me(): Observable<CurrentUserResponse> {
    return this.http
      .get<CurrentUserResponse>(`${this.apiUrl}/auth/me`, { withCredentials: true })
      .pipe(
        tap(user => this.currentUser$.next(user)),
      );
  }

  // Signal-style accessors for use in templates
  private readonly _currentUser = signal<CurrentUserResponse | null>(null);
  readonly currentUser = this._currentUser.asReadonly();

  getAccessToken(): string | null {
    return this.accessToken$.getValue();
  }

  getCurrentUser(): CurrentUserResponse | null {
    return this.currentUser$.getValue();
  }

  isStaff(): boolean {
    return isStaffUser(this.getCurrentUser());
  }

  landingUrl(requested?: string | null): string {
    return resolveLanding(this.getCurrentUser(), requested);
  }

  isCustomerAccountRoute(url?: string): boolean {
    if (typeof window === 'undefined') return false;
    const current = url || `${window.location.pathname}${window.location.search}`;
    try {
      const parsed = new URL(current, window.location.origin);
      return parsed.pathname === '/my-account' || parsed.pathname.startsWith('/my-account/');
    } catch {
      return false;
    }
  }

  redirectCustomerSessionExpired(nextUrl?: string, reason: LogoutReason = 'session-expired'): void {
    this.clearLocalSession();
    if (this.inPopupFlow()) return;
    if (typeof window !== 'undefined') {
      window.location.replace(this.customerSessionExpiredUrl(nextUrl, reason));
    }
  }

  handleSessionExpired(nextUrl?: string, reason: LogoutReason = 'session-expired'): void {
    this.clearLocalSession();
    if (this.inPopupFlow()) return;
    if (this.isCustomerAccountRoute(nextUrl)) {
      if (typeof window !== 'undefined') {
        window.location.replace(this.customerSessionExpiredUrl(nextUrl, reason));
      }
      return;
    }
    this.router.navigate(['/welcome'], { queryParams: this.reasonQuery(reason) });
  }

  hasPermission(permission: string): boolean {
    const user = this.getCurrentUser() as any;
    if (!user) return false;

    // PREFERRED PATH — backend's /auth/me resolves effective permissions
    // (direct bindings + group-inherited + platform.admin wildcard expansion)
    // and ships them as a top-level `permissions` array. This is the source
    // of truth in the group-based RBAC model where users no longer have
    // direct role bindings.
    if (Array.isArray(user.permissions)) {
      if (
        user.permissions.includes('*') ||
        user.permissions.includes('platform.admin') ||
        user.permissions.includes(permission)
      ) {
        return true;
      }
      // If the resolved array exists but doesn't grant access, that's the answer.
      // Don't fall through to the legacy direct-roles check (which would be stale).
      return false;
    }

    // LEGACY FALLBACK — old clients without resolved permissions.
    if (user.roles) {
      return user.roles.some((role: any) =>
        role.permissions?.includes(permission) ||
        role.permissions?.includes('platform.admin') ||
        role.permissions?.includes('*'),
      );
    }
    return false;
  }

  /**
   * Set session from an already-issued access token (e.g. Microsoft OAuth callback).
   * Loads /auth/me to populate currentUser.
   */
  setAccessTokenAndLoadUser(accessToken: string): Observable<CurrentUserResponse> {
    this.accessToken$.next(accessToken);
    return this.me();
  }

  private clearLocalSession(): void {
    this.accessToken$.next(null);
    this.currentUser$.next(null);
  }

  private clearSession(reason: LogoutReason = 'logout'): void {
    this.clearLocalSession();
    // Don't kick the user out to /welcome when we're inside an SSO popup
    // (URL has popup=1) or on the /sso handoff route — the popup has its
    // own state machine (msPrompt → MS button → mint → postMessage → close)
    // and any forced /welcome redirect would break the customer/employee
    // selector showing up inside the popup window.
    if (this.inPopupFlow()) return;
    if (this.isCustomerAccountRoute()) {
      if (typeof window !== 'undefined') {
        if (reason !== 'logout') {
          window.location.replace(this.customerSessionExpiredUrl(undefined, reason));
        } else {
          window.location.replace(this.websiteUrl);
        }
      }
      return;
    }
    this.router.navigate(['/welcome'], { queryParams: this.reasonQuery(reason) });
  }

  private inPopupFlow(): boolean {
    return typeof window !== 'undefined' &&
      (window.location.search.includes('popup=1') ||
       window.location.pathname.startsWith('/sso'));
  }

  private customerSessionExpiredUrl(nextUrl?: string, reason: LogoutReason = 'session-expired'): string {
    const target = new URL(this.websiteUrl);
    target.searchParams.set('sessionExpired', '1');
    target.searchParams.set('reason', reason === 'idle-timeout' ? 'idle-timeout' : 'session-expired');
    target.searchParams.set('next', this.safeCustomerNext(nextUrl));
    return target.toString();
  }

  private reasonQuery(reason: LogoutReason): Record<string, string> | undefined {
    if (reason === 'idle-timeout') return { reason: 'idle-timeout' };
    if (reason === 'expired' || reason === 'session-expired') return { reason: 'session-expired' };
    return undefined;
  }

  private safeCustomerNext(nextUrl?: string): string {
    if (typeof window === 'undefined') return '/my-account';
    try {
      const source = nextUrl || `${window.location.pathname}${window.location.search}`;
      const parsed = new URL(source, window.location.origin);
      const next = `${parsed.pathname}${parsed.search}`;
      return next === '/my-account' || next.startsWith('/my-account?') || next.startsWith('/my-account/')
        ? next
        : '/my-account';
    } catch {
      return '/my-account';
    }
  }
}
